import React, { useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';
import { css } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useMatchWidth } from '@birdi/shared-deprecated/match-width';
import { hasPermission } from '@birdi/utils/src/helpers';
import { Branding } from '@birdi/branding/branding';
import BirdiIcon, { Icons } from '@birdi/icons';
import { AvatarButton } from './Avatar';
import { MobileMenu } from './MobileMenu';
import { useMetaSelector } from '../../meta/reducer';
import { UserWorkspaces as MobileUserWorkspaces } from '../../user-workspaces/user-workspaces-mobile';
import { UserWorkspaces as DesktopUserWorkspaces } from '../../user-workspaces/user-workspaces';
import { AssistantLauncher } from '../../assistant/AssistantLauncher';
import { GlobalSearchButton } from '../../search';
import InviteSVG from '@birdi/icons/onboarding-invite.svg';

export const MainHeaderCSS = (theme: BirdiTheme) => css`
  position: relative;
  /* Above dashboard TabBar (z-index 100) so mobile menu dropdown paints on top */
  z-index: 200;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  box-sizing: border-box;
  width: 100%;
  padding: 0em 1rem;
  background: ${theme.sand};
  color: ${theme.mono70};
  font-weight: 500;
`;

/* The sidebar layout's white content card (see layouts/sidebar-layout.tsx).
   The search field lines up with its outer left edge, padding excluded. */
const CONTENT_COLUMN_SELECTOR = '.content-wrapper';

/* Brand block. On desktop it is exactly as wide as the space left of the
   dashboard content column, so the search field beside it starts on the same
   line as the white content card: sidebar column (340px + 10px margin + 8px
   gap, see layouts/sidebar-layout.tsx) minus this header's own 1rem padding.
   Everything inside that width is logo space. */
const brandCss = css`
  display: flex;
  align-items: center;

  @media (min-width: ${breakpoints.m}) {
    flex: 0 0 auto;
    box-sizing: border-box;
    width: calc(358px - 1rem);
    min-width: calc(358px - 1rem);
  }
`;

const whiteButton = css`
  color: white;
  margin-right: 1rem;
  border: 1px solid #5d5d5d;
  padding: 0.5rem;
  border-radius: 4px;
`;

export const CloudHeaderV2 = () => {
  const auth = useSelector((state) => state.auth);
  const meta = useMetaSelector();
  const mobile = useMatchWidth(breakpoints.m);
  const navigate = useNavigate();
  const brandRef = useRef<HTMLDivElement>(null);

  // Size the brand block so the search field's left edge lands exactly on
  // the white content card below it. Measured rather than computed so it
  // stays pixel-aligned with the layout; the CSS width is only the
  // first-paint guess.
  useLayoutEffect(() => {
    const brand = brandRef.current;
    if (mobile || !brand) return undefined;
    const measure = () => {
      const column = document.querySelector(CONTENT_COLUMN_SELECTOR);
      if (!column) return;
      const left =
        column.getBoundingClientRect().left -
        brand.getBoundingClientRect().left;
      if (!Number.isFinite(left) || left <= 0) return;
      brand.style.width = `${Math.round(left)}px`;
      brand.style.minWidth = brand.style.width;
    };
    measure();
    // The content column mounts after the header (it waits on meta) and
    // can reflow on data; keep re-measuring briefly, then on resize only.
    const settle = window.setInterval(measure, 250);
    const stopSettle = window.setTimeout(
      () => window.clearInterval(settle),
      3000,
    );
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => {
      window.clearInterval(settle);
      window.clearTimeout(stopSettle);
      observer.disconnect();
    };
  }, [mobile]);

  // Check if user has a plan
  const hasPlan = meta.organisation?.planV2 !== null;

  const mobileWorkspaceTrigger = (
    <button type="button">
      <BirdiIcon
        icon={Icons.Chevron}
        rotate="180"
        css={(theme: BirdiTheme) => css`
          color: ${theme.mono70};
        `}
      />
    </button>
  );

  // Without a plan the dashboard side menu — the only other place the desktop
  // workspace switcher lives — is never rendered, so surface it here.
  const switchWorkspaceTrigger = (
    <button
      type="button"
      data-testid="header-switch-workspace"
      css={(theme: BirdiTheme) => css`
        display: flex;
        align-items: center;
        gap: 0.5em;
        font-size: 1rem;
        font-weight: 400;
        color: ${theme.midnight};
        white-space: nowrap;
        cursor: pointer;
        margin-right: 1rem;
        padding: 0.75em;
        border: 1px solid ${theme.mono40};
        border-radius: 4px;
        &:hover {
          background: ${theme.lightOcean};
        }
      `}
    >
      <span>Switch workspace</span>
      <BirdiIcon icon={Icons.Chevron} rotate="180" />
    </button>
  );

  return (
    <div css={MainHeaderCSS}>
      <div css={brandCss} ref={brandRef}>
        {meta.loaded && (
          <Branding
            meta={meta}
            onClick={hasPlan ? () => navigate('/') : () => {}}
          />
        )}
        {mobile && <MobileUserWorkspaces trigger={mobileWorkspaceTrigger} />}
      </div>
      {auth.status === 'authenticated' && hasPlan && !mobile ? (
        <GlobalSearchButton variant="wide" />
      ) : (
        <div
          css={css`
            flex: 1;
          `}
        />
      )}
      <nav
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        {auth.status === 'authenticated' && hasPlan && (
          <AssistantLauncher
            css={
              /* The mobile Menu trigger is absolutely pinned to the header's
                 right corner (6rem wide at right: 1rem) — shift left of it. */
              mobile
                ? css`
                    margin-right: 6.5rem;
                  `
                : undefined
            }
          />
        )}
        {auth.status === 'anonymous' && (
          <Link
            css={css`
              margin-right: 1.5rem;
              ${whiteButton}
            `}
            to="/sign-in"
          >
            Sign in
          </Link>
        )}
        {auth.status === 'anonymous' && (
          <Link
            css={(theme: BirdiTheme) => css`
              ${whiteButton};
              background: ${theme.deepOcean};
              border-color: ${theme.deepOcean};
            `}
            to="/sign-up"
          >
            Sign up
          </Link>
        )}
        {auth.status === 'authenticated' &&
          hasPermission(meta.permissions, 'workspace:user:create') &&
          hasPlan && (
            <Link
              to="/admin-users?modal=invite"
              title="Invite a team member"
              aria-label="Invite a team member"
              data-testid="header-invite"
              css={(theme: BirdiTheme) => css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 40px;
                height: 40px;
                margin-right: 0.5rem;
                border-radius: 5px;
                color: ${theme.midnight};
                cursor: pointer;
                ${mobile ? 'display: none;' : ''}

                &:hover {
                  background: ${theme.dove};
                }
                svg {
                  width: 1.5rem;
                  height: 1.5rem;
                }
                /* The onboarding SVG hard-codes white strokes (it was drawn
                   for a dark button); draw it in midnight, the text colour. */
                svg path {
                  stroke: ${theme.midnight};
                }
              `}
            >
              <InviteSVG />
            </Link>
          )}
        {/* meta.loaded guards against a flash of this before planV2 arrives */}
        {auth.status === 'authenticated' &&
          meta.loaded &&
          !hasPlan &&
          !mobile && <DesktopUserWorkspaces trigger={switchWorkspaceTrigger} />}
        {auth.status === 'authenticated' && !mobile && <AvatarButton />}
        {auth.status === 'authenticated' && mobile && <MobileMenu />}
      </nav>
    </div>
  );
};
